import { batchFetchLogosFromSportLogosAPI, getLogoAPIRequestStats } from '../../utils/sportLogosAPI';
import { fetchLogoFromLogoAPI } from '../../utils/sportLogosAPI';
import { 
  Heading, 
  Text, 
  Box, 
  Spinner, 
  Alert, 
  AlertIcon, 
  Table, 
  Thead, 
  Tbody, 
  Tr, 
  Th, 
  Td, 
  VStack, 
  Button, 
  HStack, 
  Drawer, 
  DrawerOverlay, 
  DrawerContent, 
  DrawerHeader, 
  DrawerBody, 
  DrawerFooter, 
  FormControl, 
  FormLabel, 
  Input, 
  InputGroup, 
  InputRightElement, 
  List, 
  ListItem, 
  Badge, 
  useToast,
  SimpleGrid,
  useColorModeValue,
  ButtonGroup,
  Tag,
  TagLabel,
  Tooltip,
  Image,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Flex,
  Wrap
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import AdminLayout from '../../layouts/AdminLayout';
import { searchClubs, uploadImage, putTeamLogoOverride, fetchTeamLogoOverrides, fetchLogoAsBlob, uploadToLogaSportcreative } from '../../services/adminMatches';
import { getFacrTablesCache } from '../../services/facr/cache';
import { assetUrl } from '../../utils/url';
import { useEffect, useMemo, useRef, useState } from 'react';
import { API_URL } from '../../services/api';
 
function normalize(s: string): string {
  let out = String(s || '');
  // Normalize diacritics and case
  out = out
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
  // Unify various dash characters to a simple hyphen
  out = out.replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-');
  out = out.replace(/\bn\.?\b/g, ' nad ');
  out = out.replace(/\bp\.?\b/g, ' pod ');
  // Remove legal suffixes like ", z.s." / ", z. s." / " z.s." / "o.s." at end
  out = out.replace(/[,,\s]*(z\.?\s*s\.?|o\.?\s*s\.?)\s*$/g, '');
  // Remove organization phrases/prefixes anywhere (keep core locality/name)
  const orgPhrases = [
    'fotbalovy klub',
    'sportovni klub',
    'telovychovna jednota',
    'skolni sportovni klub',
    'spolek',
    'fotbal',
    'futsal',
  ];
  for (const phrase of orgPhrases) {
    const re = new RegExp('(^|\\b)'+ phrase + '(\\b|$)', 'g');
    out = out.replace(re, ' ');
  }
  // Remove common short prefixes (tokens) like FC, FK, MFK, TJ, SK, SFC, AFK at word boundaries
  out = out.replace(/\b(1\.)?\s*(sfc|afc|fc|fk|mfk|tj|sk|afk)\b\.?/g, ' ');
  // Remove punctuation except hyphen
  out = out.replace(/[\.,!;:()\[\]{}]/g, ' ');
  // Collapse multiple spaces and trim
  out = out.replace(/\s+/g, ' ').trim();
  return out;
}

// Derive FACR team UUID from the logo URL if team_id is missing in the row
// Example: https://is1.fotbal.cz/media/kluby/<UUID>/<UUID>_crop.jpg
function deriveTeamIdFromLogoUrl(url?: string): string | undefined {
  try {
    const u = String(url || '');
    if (!u) return undefined;
    const m = u.match(/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/);
    return m ? m[0].toLowerCase() : undefined;
  } catch {
    return undefined;
  }
}

 
type TableRow = {
  rank?: string;
  team?: string;
  team_id?: string;
  team_logo_url?: string;
  played?: string;
  wins?: string;
  draws?: string;
  losses?: string;
  score?: string;
  points?: string;
};

const TeamsAdminPage = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery<any>({
    queryKey: ['facr-tables-cache'],
    queryFn: () => getFacrTablesCache(),
    staleTime: 5 * 60 * 1000,
  });

  const competitions: any[] = Array.isArray(data?.competitions) ? data!.competitions : [];
  const mainClubId: string | undefined = (data?.club_id ? String(data.club_id).toLowerCase() : undefined);
  const mainClubBase: string = useMemo(() => normalize(String(data?.name || '')), [data?.name]);
  // Backend origin (used to resolve relative URLs like /uploads/...)
  const backendOrigin = new URL(API_URL, typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000').origin;
  const theadBg = useColorModeValue('gray.50', 'gray.700');
  const rowHoverBg = useColorModeValue('gray.50', 'gray.700');
  const searchBg = useColorModeValue('white', 'gray.800');

  // Load public/admin overrides map to apply on cache-fed view
  const { data: overrides = {} } = useQuery({
    queryKey: ['teamLogoOverrides'],
    queryFn: fetchTeamLogoOverrides,
    staleTime: 5 * 60 * 1000,
  });
  const overridesById: Record<string, { name?: string; logo_url?: string }> = (overrides as any)?.by_id || {};
  // Lowercase-key index for robust UUID lookups irrespective of source casing
  const overridesByIdLC = useMemo(() => {
    const m: Record<string, { name?: string; logo_url?: string }> = {};
    try {
      for (const [k, v] of Object.entries(overridesById)) {
        m[String(k).toLowerCase()] = v as any;
      }
    } catch {}
    return m;
  }, [overridesById]);
  // Build an index by normalized team name for overrides that carry an ID
  const overridesNameIndex = useMemo(() => {
    const idx: Record<string, { id: string; name: string; logo_url: string }> = {};
    try {
      for (const [id, v] of Object.entries(overridesById)) {
        const name = String((v as any)?.name || '').trim();
        const logo = String((v as any)?.logo_url || '').trim();
        if (!name) continue;
        const norm = normalize(name);
        if (!norm) continue;
        idx[norm] = { id, name, logo_url: logo };
      }
    } catch {}
    return idx;
  }, [overridesById]);
  
  // Fetch logos from logoapi.sportcreative.eu for all teams
  const [sportLogosMap, setSportLogosMap] = useState<Record<string, string>>({});
  const [sportLogosLoading, setSportLogosLoading] = useState(false);
  
  useEffect(() => {
    if (!competitions || competitions.length === 0) return;
    
    // Extract all unique team IDs from all competitions
    const teamIds = new Set<string>();
    for (const comp of competitions) {
      const rows: TableRow[] = comp?.table?.overall || [];
      for (const r of rows) {
        if (r.team_id) teamIds.add(String(r.team_id).toLowerCase());
        else {
          const derived = deriveTeamIdFromLogoUrl(r.team_logo_url);
          if (derived) teamIds.add(derived);
        }
      }
    }
    
    if (teamIds.size === 0) return;
    
    // Safety check: prevent excessive requests
    const MAX_TEAM_IDS = 500; // Reasonable limit to prevent request storms
    if (teamIds.size > MAX_TEAM_IDS) {
      console.warn(`Too many team IDs (${teamIds.size}) for logo fetching. Limiting to first ${MAX_TEAM_IDS}.`);
      // Keep only the first MAX_TEAM_IDS IDs
      const idsArray = Array.from(teamIds);
      idsArray.length = MAX_TEAM_IDS;
      teamIds.clear();
      idsArray.forEach(id => teamIds.add(id));
    }
    
    // Fetch logos from logoapi.sportcreative.eu with rate limiting
    setSportLogosLoading(true);
    batchFetchLogosFromSportLogosAPI(Array.from(teamIds))
      .then((map) => setSportLogosMap(map))
      .catch((err) => console.error('Failed to fetch sport logos:', err))
      .finally(() => setSportLogosLoading(false));
  }, [competitions]);
  const byName: Record<string, string> = (overrides as any)?.by_name || {};
  const byNameNormalized = useMemo(() => {
    const idx: Record<string, string> = {};
    for (const k of Object.keys(byName)) {
      idx[normalize(k)] = byName[k];
    }
    return idx;
  }, [byName]);
  const byNamePairs = useMemo(() => {
    return Object.keys(byName || {}).map((k) => ({ keyNorm: normalize(k), url: byName[k] }));
  }, [byName]);

  const getLogo = (teamName?: string, teamId?: string, original?: string) => {
    if (!teamName) return assetUrl('/dist/img/logo-club-empty.svg') as string;
    // Priority 0: Admin override by team ID
    const tid = teamId ? String(teamId).toLowerCase() : '';
    if (tid && overridesByIdLC[tid] && overridesByIdLC[tid]?.logo_url) {
      const u = String(overridesByIdLC[tid].logo_url);
      if (u.startsWith('/')) return assetUrl(u) as string;
      return u;
    }
    // Priority 0.5: Try match by override name when team_id is missing
    try {
      const norm = normalize(teamName);
      let hit = overridesNameIndex[norm];
      if (!hit) {
        // Suffix/containment match: allow sponsor words before/after core name
        for (const [keyNorm, val] of Object.entries(overridesNameIndex)) {
          if (!keyNorm) continue;
          if (norm.endsWith(keyNorm) || keyNorm.endsWith(norm)) { hit = val as any; break; }
        }
      }
      if (!hit) {
        const norm2 = normalize(teamName);
        const t1 = norm2.split(' ')[0];
        if (t1 && t1.length >= 5) {
          for (const [keyNorm, val] of Object.entries(overridesNameIndex)) {
            const k1 = String(keyNorm).split(' ')[0];
            if (k1 === t1) { hit = val as any; break; }
          }
        }
      }
      if (hit && (hit as any).logo_url) {
        const u = String((hit as any).logo_url);
        if (u.startsWith('/')) return assetUrl(u) as string;
        return u;
      }
    } catch {}
    // Priority 1: Local admin override (exact + normalized)
    let overrideUrl = byName[teamName];
    if (!overrideUrl) {
      const norm = normalize(teamName);
      overrideUrl = byNameNormalized[norm];
    }
    if (!overrideUrl) {
      // Suffix/containment against normalized keys
      const norm = normalize(teamName);
      for (const { keyNorm, url } of byNamePairs) {
        if (!keyNorm) continue;
        if (norm.endsWith(keyNorm) || keyNorm.endsWith(norm)) { overrideUrl = url; break; }
      }
    }
    if (overrideUrl) {
      if (typeof overrideUrl === 'string' && overrideUrl.startsWith('/')) {
        return assetUrl(overrideUrl) as string;
      }
      return overrideUrl;
    }

    // Priority 2: logoapi.sportcreative.eu if we have a team ID
    if (tid && sportLogosMap[tid]) {
      return sportLogosMap[tid];
    }

    // Priority 3: FACR original
    if (original) {
      return original;
    }

    // Final fallback: empty logo
    return '/dist/img/logo-club-empty.svg';
  };

  const getName = (teamName?: string, teamId?: string) => {
    const tid = teamId ? String(teamId).toLowerCase() : '';
    if (tid && overridesByIdLC[tid] && overridesByIdLC[tid]?.name) {
      return String(overridesByIdLC[tid].name || '').trim() || String(teamName || '');
    }
    // If no ID, but override exists for the normalized name, use canonical override name
    try {
      if (teamName) {
        const norm = normalize(teamName);
        let hit = overridesNameIndex[norm];
        if (!hit) {
          for (const [keyNorm, val] of Object.entries(overridesNameIndex)) {
            if (!keyNorm) continue;
            if (norm.endsWith(keyNorm) || keyNorm.endsWith(norm)) { hit = val as any; break; }
          }
        }
        if (!hit) {
          const t1 = norm.split(' ')[0];
          if (t1 && t1.length >= 5) {
            for (const [keyNorm, val] of Object.entries(overridesNameIndex)) {
              const k1 = String(keyNorm).split(' ')[0];
              if (k1 === t1) { hit = val as any; break; }
            }
          }
        }
        if (hit && (hit as any).name) {
          return (hit as any).name as string;
        }
      }
    } catch {}
    return String(teamName || '');
  };

  // View mode: 'table' per competition, or 'grid' of unique teams across competitions
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  // Selected competition for quick switching (only applies in table mode)
  const [selectedCompIndex, setSelectedCompIndex] = useState<number>(0);
  // Text filter for club/team name
  const [filter, setFilter] = useState('');
  const [filterDebounced, setFilterDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setFilterDebounced(filter), 250);
    return () => clearTimeout(t);
  }, [filter]);

  // Build a deduplicated list of teams across all competitions
  type TeamAggregate = {
    key: string; // normalized key
    name: string; // representative name
    logo: string;
    variants: string[]; // all raw names found
    teamId?: string;
  };
  const allTeamsUnique: TeamAggregate[] = useMemo(() => {
    const map: Record<string, TeamAggregate> = {};
    for (const comp of competitions) {
      const rows: TableRow[] = comp?.table?.overall || [];
      for (const r of rows) {
        const rawName = (r.team || '').trim();
        let teamId = ((r as any).team_id as string | undefined) || deriveTeamIdFromLogoUrl(r.team_logo_url);
        if (teamId) teamId = String(teamId).toLowerCase();
        if (!teamId && mainClubId) {
          const rn = normalize(rawName);
          if (
            rn === mainClubBase ||
            rn.endsWith(' ' + mainClubBase) ||
            rn.startsWith(mainClubBase + ' ') ||
            rn.includes(' ' + mainClubBase + ' ')
          ) {
            teamId = mainClubId;
          }
        }
        const canonicalName = getName(rawName, teamId);
        if (!canonicalName) continue;
        const key = teamId ? `id:${teamId}` : normalize(canonicalName);
        const logo = getLogo(canonicalName, teamId, r.team_logo_url);
        if (!map[key]) {
          map[key] = { key, name: canonicalName, logo, variants: [rawName, canonicalName], teamId };
        } else {
          map[key].variants.push(rawName);
          map[key].variants.push(canonicalName);
          // Update logo - prefer non-empty logos
          const currentIsEmpty = !map[key].logo || /logo-club-empty\.svg$/.test(String(map[key].logo));
          const newIsNotEmpty = logo && !/logo-club-empty\.svg$/.test(String(logo));
          if (currentIsEmpty && newIsNotEmpty) {
            map[key].logo = logo as string;
          }
          if (!map[key].teamId && teamId) {
            map[key].teamId = teamId;
          }
        }
      }
    }
    // Sort by representative name
    return Object.values(map).sort((a, b) => a.name.localeCompare(b.name, 'cs', { sensitivity: 'base' }));
  }, [competitions, getLogo, mainClubBase, mainClubId]);

  // Fast lookup from normalized name to variant list
  const variantsByKey = useMemo(() => {
    const m: Record<string, string[]> = {};
    for (const t of allTeamsUnique) {
      m[t.key] = Array.from(new Set(t.variants));
    }
    return m;
  }, [allTeamsUnique]);

  // Drawer state for editing a team's logo/name
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<{ compId?: string; teamName: string; teamLogoUrl?: string; variantNames?: string[] } | null>(null);
  const [form, setForm] = useState({
    external_team_id: '',
    team_name: '',
    logo_url: '',
  });
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [externalUploadStatus, setExternalUploadStatus] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle');
  const [externalUploadError, setExternalUploadError] = useState<string | null>(null);

  const showExternalUploadInfo = useMemo(() => {
    try {
      if (uploadedFile) return true;
      const raw = (form.logo_url || '').trim();
      if (!raw) return false;
      const abs = raw.startsWith('/') ? new URL(raw, backendOrigin).toString() : raw;
      const u = new URL(abs);
      const host = u.hostname.toLowerCase();
      const path = u.pathname;
      const backendHost = new URL(backendOrigin).hostname.toLowerCase();
      const isFacr = host.endsWith('fotbal.cz') || host === 'is1.fotbal.cz';
      const isLogoAPI = host === 'logoapi.sportcreative.eu';
      const isLocalUpload = (host === backendHost && path.startsWith('/uploads/'));
      return !isFacr && !isLogoAPI && isLocalUpload;
    } catch {
      return false;
    }
  }, [uploadedFile, form.logo_url, backendOrigin]);

  // Club search
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), 300);
    return () => clearTimeout(t);
  }, [query]);
  const { data: searchResults = [] } = useQuery({
    queryKey: ['club-search', debounced],
    queryFn: () => searchClubs(debounced),
    enabled: debounced.trim().length >= 2,
  });

  const onOpenEdit = (teamName: string, teamLogoUrl?: string, variantNames?: string[], teamId?: string) => {
    // If variants not explicitly provided (e.g., from table view), compute from normalized key
    let v = variantNames;
    if (!v || v.length === 0) {
      const key = normalize(teamName || '');
      v = variantsByKey[key] || [];
    }
    setSelected({ teamName, teamLogoUrl, variantNames: v });
    setForm({ external_team_id: teamId || '', team_name: teamName || '', logo_url: teamLogoUrl || '' });
    setQuery(teamName || '');
    setIsOpen(true);
  };

  const onSave = useMutation({
    mutationFn: async () => {
      let extTeamId = (form.external_team_id || '').trim();
      if (!extTeamId) {
        let derived: string | undefined = undefined;
        try { derived = deriveTeamIdFromLogoUrl(form.logo_url); } catch {}
        if (!derived && selected?.teamLogoUrl) {
          try { derived = deriveTeamIdFromLogoUrl(selected.teamLogoUrl); } catch {}
        }
        if (!derived) {
          const primaryNameTry = (form.team_name || selected?.teamName || '').trim();
          if (primaryNameTry) {
            try {
              const results = await searchClubs(primaryNameTry);
              const norm = (s: string) => normalize(s);
              const exact = results.find(r => norm(r.name) === norm(primaryNameTry));
              const pick = exact || results[0];
              if (pick?.id) derived = String(pick.id);
            } catch {}
          }
        }
        if (derived) {
          extTeamId = derived;
        }
      }
      if (!extTeamId) {
        throw new Error('Vyberte tým ze seznamu vyhledávání (chybí ID).');
      }
      let logoUrl = (form.logo_url || '').trim();
      // Prefer the edited input over the pre-selected name
      const primaryName = (form.team_name || selected?.teamName || '').trim();
      // All variants to update (deduped), always include the primary name
      const names = Array.from(new Set([primaryName, ...((selected?.variantNames || []) as string[])]))
        .map((s) => s.trim())
        .filter(Boolean);
      // Prefer highest-quality logo from logoapi if available (unless uploading a new file)
      try {
        if (!uploadedFile && extTeamId) {
          const apiLogo = await fetchLogoFromLogoAPI(extTeamId, primaryName);
          if (apiLogo) {
            logoUrl = apiLogo;
          }
        }
      } catch {}
      
      if (logoUrl) {
        let uploadAttempted = false;
        let shouldUpload = Boolean(uploadedFile);
        try {
          const abs = logoUrl.startsWith('/') ? new URL(logoUrl, backendOrigin).toString() : logoUrl;
          const u = new URL(abs);
          const host = u.hostname.toLowerCase();
          const path = u.pathname;
          const backendHost = new URL(backendOrigin).hostname.toLowerCase();
          const isFacr = host.endsWith('fotbal.cz') || host === 'is1.fotbal.cz';
          const isLogoAPI = host === 'logoapi.sportcreative.eu';
          const isLocalUpload = (host === backendHost && path.startsWith('/uploads/'));
          if (!shouldUpload) {
            shouldUpload = isLocalUpload;
          }
          if (isFacr || isLogoAPI) {
            shouldUpload = false;
          }
        } catch {}

        if (shouldUpload) {
          uploadAttempted = true;
          setExternalUploadStatus('uploading');
          setExternalUploadError(null);
          try {
            let logoFileToUpload: File | Blob | null = uploadedFile;
            if (!logoFileToUpload && logoUrl) {
              logoFileToUpload = await fetchLogoAsBlob(logoUrl);
            }
            if (logoFileToUpload) {
              const logaResult = await uploadToLogaSportcreative(
                extTeamId,
                logoFileToUpload,
                {
                  filename: `${extTeamId}.${logoFileToUpload instanceof File ? logoFileToUpload.name.split('.').pop() : 'png'}`,
                  clubName: form.team_name || selected?.teamName || 'Neznámý klub'
                }
              );
              if (logaResult.success) {
                setExternalUploadStatus('success');
                if (logaResult.url) {
                  logoUrl = logaResult.url;
                }
                try {
                  let confirmedUrl: string | null = null;
                  for (let i = 0; i < 10; i++) {
                    confirmedUrl = await fetchLogoFromLogoAPI(extTeamId, primaryName);
                    if (confirmedUrl) break;
                    await new Promise((r) => setTimeout(r, 700));
                  }
                  if (confirmedUrl) {
                    logoUrl = confirmedUrl;
                  }
                } catch {}
              } else {
                setExternalUploadStatus('error');
                setExternalUploadError(logaResult.error || 'Nepodařilo se nahrát logo');
              }
            } else {
              setExternalUploadStatus('error');
              setExternalUploadError('Could not fetch logo file');
            }
          } catch (error: any) {
            setExternalUploadStatus('error');
            setExternalUploadError(error?.message || 'Upload failed');
          }
        }

        if (uploadAttempted) {
          try {
            const abs = logoUrl.startsWith('/') ? new URL(logoUrl, backendOrigin).toString() : logoUrl;
            const host = new URL(abs).hostname.toLowerCase();
            if (host !== 'logoapi.sportcreative.eu') {
              throw new Error('Externí upload loga ještě není dostupný. Zkuste uložit znovu za chvíli.');
            }
          } catch (e: any) {
            throw new Error(e?.message || 'Externí upload loga selhal');
          }
        }
      }

      await putTeamLogoOverride(extTeamId, primaryName, logoUrl);

      return true;
    },
    onSuccess: () => {
      let description = 'Změna byla aplikována na všechny duplicitní varianty.';
      if (externalUploadStatus === 'success') {
        description += ' Logo bylo úspěšně nahráno.';
      } else if (externalUploadStatus === 'error') {
        description += ` ⚠️ ${externalUploadError || 'Nepodařilo se nahrát logo'}`;
      }
      
      toast({ 
        title: 'Uloženo', 
        description, 
        status: externalUploadStatus === 'error' ? 'warning' : 'success',
        duration: 6000,
        isClosable: true,
      });
      
      setIsOpen(false);
      setSelected(null);
      setUploadedFile(null);
      setExternalUploadStatus('idle');
      setExternalUploadError(null);
      queryClient.invalidateQueries({ queryKey: ['facr-tables-cache'] });
      queryClient.invalidateQueries({ queryKey: ['teamLogoOverrides'] });
    },
    onError: (e: any) => {
      toast({ title: 'Uložení selhalo', description: e?.message || 'Zkuste to znovu', status: 'error' });
    },
  });

  return (
    <AdminLayout requireAdmin={false}>
      <Box>
        <HStack justify="space-between" mb={4}>
          <Heading size="lg">Správa týmů</Heading>
        </HStack>
        <Text color="gray.500" mb={4} fontSize="sm">
          Přehled tabulek z FAČR. Kompaktní zobrazení s rychlým přepínáním soutěží.
        </Text>
        <Flex mb={4} gap={3} align="center" wrap="wrap">
          <ButtonGroup isAttached size="sm" variant="outline">
            <Button 
              colorScheme={viewMode === 'table' ? 'blue' : 'gray'}
              variant={viewMode === 'table' ? 'solid' : 'outline'}
              onClick={() => setViewMode('table')}
              size="sm"
            >
              Tabulky
            </Button>
            <Button 
              colorScheme={viewMode === 'grid' ? 'blue' : 'gray'}
              variant={viewMode === 'grid' ? 'solid' : 'outline'}
              onClick={() => setViewMode('grid')}
              size="sm"
            >
              Mřížka
            </Button>
          </ButtonGroup>
          <InputGroup maxW={{ base: '100%', md: '300px' }} size="sm">
            <Input placeholder="Filtrovat podle názvu týmu…" value={filter} onChange={(e) => setFilter(e.target.value)} />
            {filter && (
              <InputRightElement width="3rem">
                <Button size="xs" variant="ghost" onClick={() => setFilter('')}>Vymazat</Button>
              </InputRightElement>
            )}
          </InputGroup>
          {sportLogosLoading && (
            <Badge colorScheme="blue" fontSize="xs">
              <HStack spacing={1}>
                <Spinner size="xs" />
                <Text>Načítám loga z logoapi.sportcreative.eu...</Text>
              </HStack>
            </Badge>
          )}
          {!sportLogosLoading && Object.keys(sportLogosMap).length > 0 && (
            <Badge colorScheme="green" fontSize="xs">
              ✓ {Object.keys(sportLogosMap).length} log z logoapi.sportcreative.eu
            </Badge>
          )}
          {Object.keys(sportLogosMap).length > 0 && (
            <Badge colorScheme="blue" fontSize="xs">
              📊 {getLogoAPIRequestStats().totalRequests}/min požadavků
            </Badge>
          )}
        </Flex>

      {isLoading && (
        <VStack align="start" spacing={3} mb={6}>
          <Spinner />
          <Text>Načítám tabulky…</Text>
        </VStack>
      )}

      {Boolean(error) && (
        <Alert status="error" variant="left-accent" mb={4}>
          <AlertIcon />
          Nepodařilo se načíst data z cache.
        </Alert>
      )}

      {!isLoading && !error && viewMode === 'table' && (
        <Box>
          <Tabs 
            index={selectedCompIndex} 
            onChange={setSelectedCompIndex}
            variant="soft-rounded"
            colorScheme="blue"
            size="sm"
          >
            <TabList mb={4} overflowX="auto" overflowY="hidden" flexWrap="nowrap" pb={2}>
              {competitions.map((comp, idx) => (
                <Tab 
                  key={comp.id} 
                  fontSize="xs"
                  px={3}
                  py={1.5}
                  minW="fit-content"
                  whiteSpace="nowrap"
                >
                  {comp.name}
                </Tab>
              ))}
            </TabList>
            <TabPanels>
              {competitions.map((comp) => {
                const rows: TableRow[] = comp?.table?.overall || [];
                const f = normalize(filterDebounced);
                const rowsFiltered = f ? rows.filter((r) => normalize(r.team || '').includes(f)) : rows;
                return (
                  <TabPanel key={comp.id} p={0}>
                    <Box
                      overflowX="auto"
                      borderWidth="1px"
                      borderRadius="md"
                      w="full"
                      sx={{
                        WebkitOverflowScrolling: 'touch',
                      }}
                    >
                      <Table size="sm" variant="simple">
                        <Thead bg={theadBg}>
                          <Tr>
                            <Th w="40px" fontSize="xs" py={2}>#</Th>
                            <Th fontSize="xs" py={2}>Tým</Th>
                            <Th isNumeric w="45px" fontSize="xs" py={2}>Z</Th>
                            <Th isNumeric w="45px" fontSize="xs" py={2}>V</Th>
                            <Th isNumeric w="45px" fontSize="xs" py={2}>R</Th>
                            <Th isNumeric w="45px" fontSize="xs" py={2}>P</Th>
                            <Th isNumeric w="70px" fontSize="xs" py={2}>Skóre</Th>
                            <Th isNumeric w="50px" fontSize="xs" py={2}>Body</Th>
                            <Th w="90px" fontSize="xs" py={2}>Akce</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {rowsFiltered.map((r, idx) => (
                            <Tr key={`${comp.id}-${idx}`} _hover={{ bg: rowHoverBg }}>
                              <Td py={1.5} fontSize="xs">{r.rank}</Td>
                              <Td py={1.5}>
                                <HStack spacing={2} align="center">
                                  <Image
                                    src={getLogo(r.team, ((r as any).team_id as any) || deriveTeamIdFromLogoUrl(r.team_logo_url), r.team_logo_url)}
                                    alt={getName(r.team, ((r as any).team_id as any) || deriveTeamIdFromLogoUrl(r.team_logo_url))}
                                    boxSize="24px"
                                    objectFit="contain"
                                  />
                                  <Text fontSize="xs" noOfLines={1}>{getName(r.team, ((r as any).team_id as any) || deriveTeamIdFromLogoUrl(r.team_logo_url))}</Text>
                                </HStack>
                              </Td>
                              <Td isNumeric py={1.5} fontSize="xs">{r.played}</Td>
                              <Td isNumeric py={1.5} fontSize="xs">{r.wins}</Td>
                              <Td isNumeric py={1.5} fontSize="xs">{r.draws}</Td>
                              <Td isNumeric py={1.5} fontSize="xs">{r.losses}</Td>
                              <Td isNumeric py={1.5} fontSize="xs">{r.score}</Td>
                              <Td isNumeric py={1.5} fontSize="xs" fontWeight="bold">{r.points}</Td>
                              <Td py={1.5}>
                                <Button size="xs" fontSize="xs" onClick={() => {
                                  const tidRaw = ((r as any).team_id as any) || deriveTeamIdFromLogoUrl(r.team_logo_url);
                                  const tid = tidRaw ? String(tidRaw).toLowerCase() : undefined;
                                  const displayName = getName(r.team, tid);
                                  const key = tid ? `id:${tid}` : normalize(displayName);
                                  onOpenEdit(displayName || '', getLogo(r.team, tid, r.team_logo_url), variantsByKey[key], tid);
                                }}>Upravit</Button>
                              </Td>
                            </Tr>
                          ))}
                          {rowsFiltered.length === 0 && (
                            <Tr>
                              <Td colSpan={9} py={4}>
                                <Text color="gray.500" fontSize="sm" textAlign="center">Žádný tým neodpovídá filtru.</Text>
                              </Td>
                            </Tr>
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  </TabPanel>
                );
              })}
            </TabPanels>
          </Tabs>
        </Box>
      )}

      {!isLoading && !error && viewMode === 'grid' && (
        <Box>
          {(() => {
            const f = normalize(filterDebounced);
            const source = f
              ? allTeamsUnique.filter((t) => normalize(t.name).includes(f) || (t.variants || []).some(v => normalize(v).includes(f)))
              : allTeamsUnique;
            return (
              <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 6 }} spacing={3}>
                {source.map((t) => (
                  <Box 
                    key={t.key} 
                    borderWidth="1px" 
                    borderRadius="md" 
                    p={2.5} 
                    _hover={{ boxShadow: 'md', borderColor: 'blue.300' }}
                    transition="all 0.2s"
                  >
                    <VStack align="stretch" spacing={2}>
                      <HStack justify="space-between" align="start">
                        <HStack spacing={2} flex={1} minW={0}>
                          <Image src={t.logo} alt={t.name} boxSize="24px" objectFit="contain" flexShrink={0} />
                          <Text fontSize="xs" noOfLines={2} fontWeight="medium">{t.name}</Text>
                        </HStack>
                        {t.variants.length > 1 && (
                          <Tooltip label={`Varianty: ${Array.from(new Set(t.variants)).join(', ')}`} hasArrow>
                            <Tag size="sm" colorScheme="purple" variant="subtle" fontSize="xs">
                              <TagLabel>{t.variants.length}</TagLabel>
                            </Tag>
                          </Tooltip>
                        )}
                      </HStack>
                      <Button size="xs" fontSize="xs" onClick={() => onOpenEdit(t.name, t.logo, t.variants, t.teamId)} w="full">Upravit</Button>
                    </VStack>
                  </Box>
                ))}
                {source.length === 0 && (
                  <Box gridColumn={{ base: 'span 2', sm: 'span 3', md: 'span 4', lg: 'span 6' }}>
                    <Text color="gray.500" fontSize="sm" textAlign="center" py={8}>Žádný tým neodpovídá filtru.</Text>
                  </Box>
                )}
              </SimpleGrid>
            );
          })()}
        </Box>
      )}

      <Drawer isOpen={isOpen} placement="right" onClose={() => { setIsOpen(false); setSelected(null); }} size="md">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader>Upravit logo týmu</DrawerHeader>
          <DrawerBody>
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Hledat tým (FAČR)</FormLabel>
                <InputGroup>
                  <Input value={query} onChange={(e) => { setQuery(e.target.value); }} placeholder="Zadejte název týmu" />
                  <InputRightElement width="8rem">
                    <Button size="xs" onClick={() => fileRef.current?.click()}>Nahrát logo</Button>
                  </InputRightElement>
                </InputGroup>
                {searchResults.length > 0 && (
                  <Box 
                    mt={4} 
                    bg={searchBg}
                    borderWidth="1px" 
                    borderRadius="lg" 
                    overflowX="auto"
                    boxShadow="sm"
                    mb={6}
                  >
                    <List spacing={0}>
                      {searchResults.map((r: any) => (
                        <ListItem
                          key={r.id}
                          px={3}
                          py={2}
                          _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                          onClick={async () => {
                            setForm((f) => ({ ...f, external_team_id: r.id, team_name: r.name, logo_url: r.logo_url || f.logo_url }));
                            setQuery(r.name);
                            try {
                              const apiUrl = await fetchLogoFromLogoAPI(r.id, r.name);
                              if (apiUrl) {
                                setForm((f) => ({ ...f, logo_url: apiUrl }));
                              }
                            } catch {}
                          }}
                        >
                          <HStack justify="space-between" spacing={3}>
                            <HStack spacing={3} maxW="80%">
                              {r.logo_url ? (
                                <Image src={r.logo_url} alt={r.name} boxSize="20px" objectFit="contain" />
                              ) : (
                                <Badge colorScheme="gray">bez loga</Badge>
                              )}
                              <Text noOfLines={1}>{r.name}</Text>
                            </HStack>
                            {r.logo_url && <Badge colorScheme="green">logo</Badge>}
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                <input ref={fileRef} type="file" accept="image/png,image/svg+xml,image/jpeg,application/pdf" style={{ display: 'none' }} onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  try {
                    const { url } = await uploadImage(file);
                    setForm((f) => ({ ...f, logo_url: url }));
                    setUploadedFile(file); // Store for later upload to external API
                    toast({ title: 'Logo nahráno', status: 'success' });
                  } catch (err: any) {
                    toast({ title: 'Nahrávání selhalo', description: err?.message || 'Zkuste to znovu', status: 'error' });
                  } finally {
                    if (fileRef.current) fileRef.current.value = '' as any;
                  }
                }} />
              </FormControl>

              <FormControl>
                <FormLabel>Název týmu</FormLabel>
                <Input value={form.team_name} onChange={(e) => setForm({ ...form, team_name: e.target.value })} />
              </FormControl>

              <FormControl>
                <FormLabel>Logo URL</FormLabel>
                <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
              </FormControl>
              {selected?.variantNames && selected.variantNames.length > 1 && (
                <Alert status="info">
                  <AlertIcon />
                  Upravíte také duplicitní názvy: {Array.from(new Set(selected.variantNames)).join(', ')}
                </Alert>
              )}
              {showExternalUploadInfo && (
                <Alert status="info" variant="left-accent">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">Logo bude automaticky nahráno na logoapi.sportcreative.eu</Text>
                    <Text fontSize="xs" color="gray.600">Toto poskytuje zálohu a sdílení log mezi aplikacemi.</Text>
                  </VStack>
                </Alert>
              )}
            </VStack>
          </DrawerBody>
          <DrawerFooter>
            <HStack>
              <Button variant="outline" onClick={() => { setIsOpen(false); setSelected(null); }}>Zrušit</Button>
              <Button colorScheme="blue" isLoading={onSave.isPending} onClick={() => onSave.mutate()}>Uložit</Button>
            </HStack>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      </Box>
    </AdminLayout>
  );
};

export default TeamsAdminPage;
